import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useTag } from '@react/hooks';
import type { TagVisibility } from '@core/types/tag';
import { Alert, Badge, Button, Card, CardContent, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PaneControls } from '@/features/tabs/PaneChrome';

export const TagDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTag();
  const { t } = useI18n();
  const item = store.currentItem;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) void store.GetTag(id).catch(() => {});
    return () => store.clearCurrent();
  }, [id, store]);

  const visibilityLabel = (visibility?: TagVisibility) => {
    if (visibility === 'TAG_VISIBILITY_PERSONAL') return t('tag.personal');
    if (visibility === 'TAG_VISIBILITY_TEAM') return t('tag.team');
    return t('tag.tenant');
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await store.DeleteTag(id);
      navigate('/tags');
    } catch {
      setConfirmDelete(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (store.isLoading) return <p className="text-muted-foreground">{t('common.loading')}</p>;
  if (store.error && !item) return <ErrorAlert error={store.error} />;
  if (!item) return <Alert variant="warning" title={t('common.notFound')}><Link to="/tags">{t('tag.backToTags')}</Link></Alert>;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PaneControls>
        <Link to={`/tags/${id}/edit`}><Button size="sm" variant="secondary">{t('common.edit')}</Button></Link>
      </PaneControls>
      <Link to="/tags" className="text-sm text-muted-foreground hover:text-foreground">{t('tag.backToTags')}</Link>
      <div className="flex items-center gap-3">
        <span className="h-5 w-5 shrink-0 rounded-full border border-border" style={{ backgroundColor: item.color }} />
        {item.icon && <span aria-hidden="true">{item.icon}</span>}
        <h1 className="min-w-0 truncate text-xl font-semibold text-foreground">{item.name}</h1>
        <Badge variant="secondary" size="sm">{visibilityLabel(item.visibility)}</Badge>
      </div>
      {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}
      <Card>
        <CardContent className="grid gap-5 p-5 sm:grid-cols-2">
          <div>
            <p className="text-xs text-muted-foreground">{t('tag.usage')}</p>
            <p className="mt-1 font-medium text-foreground">{t('tag.usageCount', { count: item.usage_count })}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('tag.scope')}</p>
            <p className="mt-1 font-medium text-foreground">
              {item.visibility === 'TAG_VISIBILITY_TEAM' ? t('tag.team') : item.scope_product_id ? t('tag.productScoped') : t('tag.allProducts')}
            </p>
          </div>
        </CardContent>
      </Card>
      <div className="border-t border-border pt-5">
        <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>{t('tag.delete')}</Button>
      </div>
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <ConfirmDialog
        open={confirmDelete}
        title={t('tag.delete')}
        description={t('tag.deleteConfirm')}
        confirmLabel={t('tag.delete')}
        destructive
        isConfirming={isDeleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
});

export default TagDetailPage;
